import React, { memo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { ChildrenPropType } from '../../../../../../prop-types/children';
import Global from '../../../../../../store/global-selectors';
import { FulfillmentDisplayContext } from '../../../../fulfillment/display-context';
import { useExternalFulfillmentDisplay } from '../../../../fulfillment/external-fulfillment-display/use-external-fulfillment-display';

const externalFulfillmentDetailsSelector = createSelector(
  (state, { fulfillmentId }) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId }) || emptyObject,
  (state, { fulfillmentPlanId }) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || emptyObject,
  (state) => Global.selectSellerAllFulfillmentProviders(state) || emptyArray,
  (fulfillment, fulfillmentPlan, sellerFulfillmentProviders) => {
    const carrierName = fulfillment.externalFulfillment?.carrier?.name || fulfillment.externalFulfillment?.carrierCode;
    const { name: fulfillmentProviderName } =
      sellerFulfillmentProviders.find((sfp) => sfp.sellerFulfillmentProviderId === fulfillment.sellerFillProviderId) || {};
    const delegationDate = fulfillment.createDateTime;
    const rate = fulfillmentPlan.rateSummary?.rate?.totalCost;
    const fulfillmentServiceId = fulfillment.externalFulfillment?.serviceId;
    const shipDate = fulfillment.trackingInformation?.shipDate;
    const trackingNumber = fulfillment.trackingInformation?.trackingNumber;
    const trackingUrl = fulfillment.trackingInformation?.url;

    // tracking status inputs
    const errorMessage = fulfillment.externalFulfillment?.externalFulfillmentError;
    const externalFulfillmentStatus = fulfillment.externalFulfillment?.externalFulfillmentStatus;
    const isVoided = fulfillment.externalFulfillment?.isVoided;
    const isVoidRequested = fulfillment.externalFulfillment?.isVoidRequested;
    const trackingStatus = fulfillment.trackingInformation?.status;

    return {
      carrierName,
      delegationDate,
      errorMessage,
      externalFulfillmentStatus,
      fulfillmentProviderName,
      isVoided,
      isVoidRequested,
      rate,
      fulfillmentServiceId,
      shipDate,
      trackingNumber,
      trackingStatus,
      trackingUrl,
    };
  },
);

const ExternalFulfillmentDisplayProvider = ({ children, fulfillmentPlanId, fulfillmentId }) => {
  const externalFulfillmentDetails = useSelector((state) =>
    externalFulfillmentDetailsSelector(state, {
      fulfillmentId,
      fulfillmentPlanId,
    }),
  );

  const value = useExternalFulfillmentDisplay(externalFulfillmentDetails);

  return <FulfillmentDisplayContext.Provider value={value}>{children}</FulfillmentDisplayContext.Provider>;
};

export default memo(ExternalFulfillmentDisplayProvider);

ExternalFulfillmentDisplayProvider.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  fulfillmentId: PropTypes.string.isRequired,
  children: ChildrenPropType.isRequired,
};
